import React, { useState, useEffect } from "react";
import { Button, Icon, Modal, Grid, Segment, Popup, Responsive, Message, Label, Step, Loader, Dimmer, Header } from "semantic-ui-react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { displayGender } from "utils/displayGender";
import displayName from "utils/displayName";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import MainButton from "components/Buttons/MainButton";
import CustomPagination from "components/CustomPagination";
import moment from "moment-with-locales-es6";
import debounce from "debounce-promise";

moment.locale("fr");

const isMobile = () => {
	return window.innerWidth - Responsive.onlyMobile.maxWidth < 0;
};

const schemaValidation = Yup.object().shape({
	matricule: Yup.string(),
});

const styles = {
	rowTitle: {
		marginTop: "12px",
		paddingTop: "12px",
		color: "#fff",
		backgroundColor: "teal",
		fontWeight: "bold",
		fontSize: "0.9em",
		alignItems: "center",
	},
	titleGrid: {
		paddingLeft: "0",
	},
	rowEven: {
		padding: 0,
		backgroundColor: "#fff",
	},
	rowOdd: {
		padding: 0,
		backgroundColor: "#00808021",
	},
	patientLabelGrid: {
		textAlign: "right",
		paddingRight: "10px",
		fontWeight: "bold",
		fontSize: "0.9em",
		width: "150px",
		padding: "3px 3px",
	},
	patientValueGrid: {
		textAlign: "left",
		fontSize: "0.9em",
		wordWrap: "break-word",
		padding: "3px 3px",
	},
	patientValueGridResult: {
		textAlign: "left",
		fontSize: "1.1em",
		fontWeight: "bold",
		wordWrap: "break-word",
		wordBreak: "break-all",
		borderBottom: "0px",
		borderTop: "0px",
		borderRight: "4px solid darkorange",
		borderLeft: "4px solid darkorange",
		padding: "3px 3px",
	},
	patientList: {
		backgroundColor: "#fff",
		width: "100%",
		position: "relative",
		padding: 10,
		display: "flex",
		borderRadius: "0",
		marginTop: "5px",
		boxShadow: "0px 9px 35px -23px rgba(148,148,148,1)",
		alignItems: "center",
	},
	patientListSelected: {
		backgroundColor: "#00808021",
		width: "100%",
		position: "relative",
		padding: 10,
		display: "flex",
		borderRadius: "0",
		marginTop: "5px",
		alignItems: "center",
		border: "4px solid teal",
		borderBottom: 0,
		boxShadow: "none",
	},
	patientListSelectedData: {
		backgroundColor: "#00808021",
		width: "100%",
		position: "relative",
		padding: 10,
		display: "flex",
		borderRadius: "0",
		boxShadow: "0px 9px 35px -23px rgba(148,148,148,1)",
		alignItems: "center",
		border: "4px solid teal",
		borderTop: 0,
	},

	sectionTitle: {
		fontSize: "14px",
		display: "flex",
		justifyContent: "center",
		padding: "5px 20px",
		color: "teal",
		borderRadius: "7px",
		border: "2px solid teal",
		fontWeight: "bolder",
	},
	roundHeaderContainer: {
		backgroundColor: "#43b39e",
		width: "100%",
		padding: 10,
		display: "flex",
		borderRadius: "4px 4px 0 0",
		boxShadow: "0px 9px 35px -23px rgba(148,148,148,1)",
		position: "relative",
	},
	columnFlex: {
		display: isMobile() ? "contents" : "flex",
		height: isMobile() ? "initial" : "100%",
		alignItems: "center",
	},
	titleInfo: {
		paddingRight: "10px",
		fontWeight: "bold",
		fontSize: "0.9em",
		width: "150px",
		padding: "3px 2px",
	},
};

const CampaignPatientMatchingModal = ({
	open,
	close,
	submit,
	data,
	mpi,
	mylabList,
	mylabPagination,
	searchByMatricule,
	generalParameters,
	loading,
}) => {
	const [step, setStep] = useState(1);
	const [mylab, setMylab] = useState(null);
	const [larger, setLarger] = useState(false);
	const [page, setPage] = useState(1);
	useEffect(() => {
		setPage((mylabPagination.start - 1) / 20 + 1);
	}, [mylabPagination]);

	const patientData = (patient, type) => {
		if (patient)
			return (
				<Grid key={`grid-${patient.id}-${type}`}>
					<Grid.Row columns={2}>
						{patient.is_dead && patient.is_dead !== "N" && (
							<Grid.Column width={16} style={{ display: "flex", justifyContent: "center" }}>
								<span style={{ color: "red" }}>! personne décédée !</span>
							</Grid.Column>
						)}
						<Grid.Column style={styles.patientLabelGrid}> Nom de naissance: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}> {patient.birth_name || (type === "mylab" && patient.last_name) || "-"}</Grid.Column>
						<Grid.Column style={styles.patientLabelGrid}> Nom marital: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>
							{" "}
							{(type === "mylab" && patient.marital_name) || (type !== "mylab" && patient.last_name) || "-"}
						</Grid.Column>
						<Grid.Column style={styles.patientLabelGrid}> Prénom: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>{patient.first_name || "-"}</Grid.Column>
						<Grid.Column style={styles.patientLabelGrid}> Genre: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>
							{(patient.gender === "male" || patient.gender === "m" || patient.gender === "M") && "Homme"}{" "}
							{patient.gender !== "male" && patient.gender !== "m" && patient.gender !== "M" && "Femme"}
						</Grid.Column>
						<Grid.Column style={styles.patientLabelGrid}> Date de naissance: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>
							{patient.birth_date
								? new RegExp("^(0?[1-9]|[12][0-9]|3[01])[/](0?[1-9]|1[012])[/]([1-9]{1}[0-9]{3})$").test(patient.birth_date.toString())
									? moment(patient.birth_date, "DD/MM/YYYY").format("DD MMMM YYYY")
									: moment(patient.birth_date).format("DD MMMM YYYY")
								: "-"}
						</Grid.Column>
						<Grid.Column style={styles.patientLabelGrid}> Téléphone: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>{patient.mobile_phone || patient.mobilephone || patient.phone} </Grid.Column>
						<Grid.Column style={styles.patientLabelGrid}> Email: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>{patient.email || "-"}</Grid.Column>
						{/* <Grid.Column style={styles.patientLabelGrid}> N° pièce d'identité: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>{patient.identity_number || "-"}</Grid.Column> */}
						<Grid.Column style={styles.patientLabelGrid}> Matricule: </Grid.Column>
						<Grid.Column style={styles.patientValueGrid}>{patient.matricule || patient.cns || "-"}</Grid.Column>
					</Grid.Row>
				</Grid>
			);
	};

	const patientDataUpdated = (patientForm, patientMyLab, mpi, updates, values) => {
		let mylab = !!patientMyLab;
		if (patientForm)
			return (
				<Grid key={`grid-${patientForm.id}`}>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowTitle}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} tablet={mylab || mpi ? 4 : 6} style={styles.titleGrid}>
							{" "}
							Formulaire Patient
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} tablet={4} style={styles.titleGrid}>
								{" "}
								MyLab{" "}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} tablet={4} style={styles.titleGrid}>
								{" "}
								Annuaire national des patients{" "}
							</Grid.Column>
						)}
						<Grid.Column
							computer={mylab || mpi ? 5 : 7}
							tablet={mylab || mpi ? 5 : 7}
							style={{ ...styles.titleGrid, color: "darkorange", textTransform: "uppercase" }}
						>
							Données que vous conservez pour ce PATIENT <Icon name="arrow alternate circle down" size="large" color="orange" />{" "}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowEven}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Nom de naissance:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{" "}
							{patientForm.birth_name || "-"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{" "}
								{patientMyLab.birth_name || patientMyLab.last_name || "-"}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{" "}
								{mpi.birth_name || mpi.last_name || "-"}
							</Grid.Column>
						)}

						<Grid.Column
							computer={mylab || mpi ? 5 : 7}
							style={{ ...styles.patientValueGridResult, borderTop: "4px solid darkorange", borderBottom: 0 }}
						>
							{" "}
							{mylab
								? patientMyLab.birth_name || patientMyLab.last_name || "-"
								: updates.filter((f) => f.name === "birth_name").map((field) => radio(field.name, field.label, field.fields, values))}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowOdd}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Nom marital:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{" "}
							{patientForm.last_name || "-"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{" "}
								{patientMyLab.marital_name || "-"}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{" "}
								{mpi.marital_name || "-"}
							</Grid.Column>
						)}
						<Grid.Column computer={mylab || mpi ? 5 : 7} style={styles.patientValueGridResult}>
							{" "}
							{mylab
								? patientMyLab.marital_name || "-"
								: updates.filter((f) => f.name === "married_name").map((field) => radio(field.name, field.label, field.fields, values)) || "-"}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowEven}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Prénom(s):{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{patientForm.first_name || "-"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{patientMyLab.first_name || "-"}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{mpi.first_name || "-"}
							</Grid.Column>
						)}

						<Grid.Column computer={mylab || mpi ? 5 : 7} style={styles.patientValueGridResult}>
							{mylab
								? patientMyLab.first_name || "-"
								: updates.filter((f) => f.name === "first_name").map((field) => radio(field.name, field.label, field.fields, values))}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowOdd}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Genre:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{(patientForm.gender === "male" || patientForm.gender === "m" || patientForm.gender === "M") && "Homme"}{" "}
							{patientForm.gender !== "male" && patientForm.gender !== "m" && patientForm.gender !== "M" && "Femme"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{(patientMyLab.gender === "male" || patientMyLab.gender === "m" || patientMyLab.gender === "M") && "Homme"}{" "}
								{patientMyLab.gender !== "male" && patientMyLab.gender !== "m" && patientMyLab.gender !== "M" && "Femme"}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{(mpi.gender === "male" || mpi.gender === "m" || mpi.gender === "M") && "Homme"}{" "}
								{((mpi.gender === "female" && mpi.gender === "f") || mpi.gender === "F") && "Femme"}
							</Grid.Column>
						)}

						<Grid.Column computer={mylab || mpi ? 5 : 7} style={styles.patientValueGridResult}>
							{mylab
								? ((patientMyLab.gender === "male" || patientMyLab.gender === "m" || patientMyLab.gender === "M") && "Homme") ||
								  (patientMyLab.gender !== "male" && patientMyLab.gender !== "m" && patientMyLab.gender !== "M" && "Femme")
								: updates.filter((f) => f.name === "gender").map((field) => radio(field.name, field.label, field.fields, values)) || "-"}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowEven}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Date de naissance:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{patientForm.birth_date ? moment(patientForm.birth_date, "DD/MM/YYYY").format("DD MMMM YYYY") : "-"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{patientMyLab.birth_date ? moment(patientMyLab.birth_date).format("DD MMMM YYYY") : "-"}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{mpi.birth_date ? moment(mpi.birth_date).format("DD MMMM YYYY") : "-"}
							</Grid.Column>
						)}

						<Grid.Column computer={mylab || mpi ? 5 : 7} style={styles.patientValueGridResult}>
							{mylab
								? patientMyLab.birth_date
									? moment(patientMyLab.birth_date).format("DD MMMM YYYY")
									: "-"
								: updates.filter((f) => f.name === "birth_date").map((field) => radio(field.name, field.label, field.fields, values)) || "-"}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowOdd}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Téléphone:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{patientForm.mobile_phone || patientForm.mobilephone || "-"}{" "}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{patientMyLab.mobile_phone || patientMyLab.mobilephone || "-"}{" "}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{mpi.mobile_phone || mpi.mobilephone || mpi.phone || "-"}{" "}
							</Grid.Column>
						)}

						<Grid.Column computer={mylab || mpi ? 5 : 7} style={styles.patientValueGridResult}>
							{updates
								.filter((f) => f.name === "mobile_phone" || f.name === "phone")
								.map((field) => radio(field.name, field.label, field.fields, values)) || "-"}{" "}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowEven}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Email:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{patientForm.email || "-"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{patientMyLab.email || "-"}{" "}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{mpi.email || "-"}{" "}
							</Grid.Column>
						)}

						<Grid.Column computer={mylab || mpi ? 5 : 7} style={styles.patientValueGridResult}>
							{updates.filter((f) => f.name === "email").map((field) => radio(field.name, field.label, field.fields, values)) || "-"}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowOdd}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							N° pièce d'identité:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{patientForm.identity_number || "-"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{patientMyLab.identity_number || "-"}{" "}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{mpi.identity_number || "-"}{" "}
							</Grid.Column>
						)}

						<Grid.Column computer={mylab || mpi ? 5 : 7} style={styles.patientValueGridResult}>
							{updates.filter((f) => f.name === "identity_number").map((field) => radio(field.name, field.label, field.fields, values) || "-")}
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={mylab || mpi ? 4 : 3} style={styles.rowEven}>
						<Grid.Column computer={3} tablet={3} style={styles.patientLabelGrid}>
							{" "}
							Matricule:{" "}
						</Grid.Column>
						<Grid.Column computer={mylab || mpi ? 4 : 6} style={styles.patientValueGrid}>
							{patientForm.matricule || patientForm.cns || "-"}
						</Grid.Column>
						{mylab && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{patientMyLab.matricule || patientMyLab.cns || "-"}{" "}
							</Grid.Column>
						)}
						{!mylab && mpi && (
							<Grid.Column computer={4} style={styles.patientValueGrid}>
								{mpi.matricule || mpi.cns || "-"}{" "}
							</Grid.Column>
						)}

						<Grid.Column computer={mylab || mpi ? 5 : 7} style={{ ...styles.patientValueGridResult, borderBottom: "4px solid darkorange" }}>
							{mylab
								? patientMyLab.matricule || patientMyLab.cns || "-"
								: updates.filter((f) => f.name === "cns").map((field) => radio(field.name, field.label, field.fields, values)) || "-"}
						</Grid.Column>
					</Grid.Row>
				</Grid>
			);
	};

	const defineFields = (patient, mylab, mpi) => {
		let result = [
			{
				name: "birth_name",
				label: "Nom de naissance",
				fields: [
					{ id: "newPatient", customlabel: patient.birth_name },
					{ id: "mylab", customlabel: mylab?.birth_name ?? mylab?.last_name },
					{ id: "mpi", customlabel: mpi?.birth_name },
				],
				isMyLab: false,
			},
			{
				name: "married_name",
				label: "Nom marital",
				fields: [
					{ id: "newPatient", customlabel: patient.last_name },
					{ id: "mylab", customlabel: mylab?.marital_name ?? mylab?.last_name },
					{ id: "mpi", customlabel: mpi?.marital_name },
					{ id: "void", customlabel: "Ne pas renseigner" },
				],
				isMyLab: false,
			},
			{
				name: "first_name",
				label: "Prénom(s)",
				fields: [
					{ id: "newPatient", customlabel: patient.first_name },
					{ id: "mylab", customlabel: mylab?.first_name },
					{ id: "mpi", customlabel: mpi?.first_name },
				],
				isMyLab: false,
			},
			{
				name: "gender",
				label: "Genre",
				fields: [
					{
						id: "newPatient",
						customlabel:
							data.patient.gender === "male" || data.patient.gender === "m"
								? "Homme"
								: data.patient.gender !== "male" && data.patient.gender !== "m"
								? "Femme"
								: "Autre",
					},
					{
						id: "mylab",
						customlabel: mylab
							? mylab?.gender === "male" || mylab?.gender === "m"
								? "Homme"
								: mylab?.gender !== "male" && mylab?.gender !== "m"
								? "Femme"
								: "Autre"
							: null,
					},
					{ id: "mpi", customlabel: mpi ? (mpi?.gender === "M" ? "Homme" : mpi?.gender === "F" ? "Femme" : "Autre") : null },
				],
				isMyLab: false,
			},
			{
				name: "birth_date",
				label: "Date de naissance",
				fields: [
					{ id: "newPatient", customlabel: moment(data.patient.birth_date, "DD/MM/YYYY").format("DD MMMM YYYY") },
					{ id: "mylab", customlabel: mylab ? moment(mylab.birth_date).format("DD MMMM YYYY") : null },
					{ id: "mpi", customlabel: mpi ? moment(mpi?.birth_date).format("DD MMMM YYYY") : null },
				],
				isMyLab: false,
			},
			{
				name: "mobile_phone",
				label: "Téléphone",
				fields: [
					{ id: "newPatient", customlabel: patient.mobile_phone },
					{ id: "mylab", customlabel: mylab?.mobile_phone },
					{ id: "mpi", customlabel: mpi?.mobile_phone || mpi?.phone },
				],
				isMyLab: true,
			},
			{
				name: "email",
				label: "Email",
				fields: [
					{ id: "newPatient", customlabel: patient.email },
					{ id: "mylab", customlabel: mylab?.email },
					{ id: "mpi", customlabel: mpi?.email },
				],
				isMyLab: true,
			},
			{
				name: "identity_number",
				label: "N° pièce d'identité",
				fields: [
					{ id: "newPatient", customlabel: patient.identity_number },
					{ id: "mylab", customlabel: mylab?.identification },
					{ id: "mpi", customlabel: mpi?.identity_number },
					//{id:'void', customlabel:'Ne pas renseigner'}
				],
				isMyLab: true,
			},
			{
				name: "cns",
				label: "Matricule",
				fields: [
					{ id: "newPatient", customlabel: patient.matricule || patient.cns },
					{ id: "mylab", customlabel: mylab?.cns },
					{ id: "mpi", customlabel: mpi?.cns },
				],
				isMyLab: false,
			},
		];
		return result;
	};

	const getModifiedFields = (patient, mylab, mpi, isMyLab) => {
		const result = defineFields(patient, mylab, mpi)
			.map((field) => {
				return {
					field: field.name,
					label: field.label,
					values: field.fields.filter(
						(f, index, arr) =>
							field.isMyLab === isMyLab &&
							f.customlabel !== undefined &&
							f.customlabel !== null &&
							f.id !== "mpi" &&
							field.id !== f.id &&
							arr.filter((f1, i) => f1.customlabel !== undefined && f1.customlabel !== f.customlabel && index > i).length > 0
					),
				};
			})
			.filter((r) => r.values.length > 0);
		return result;
	};

	const handleUsePatient = (values) => {
		let patientResult;
		if (mylab) {
			patientResult = { ...mylab, mylab_id: mylab.id };
			delete patientResult.id;
			patientResult = updatePatient(patientResult, "mylab", data.patient, mpi, mylab, values);
		} else {
			patientResult = data.patient;
			patientResult = updatePatient(patientResult, "newPatient", data.patient, mpi, mylab, values);
		}
		submit(data.record.id, patientResult);
		closeModal(true);
	};
	const handleCreatePatient = (values) => {
		let patientResult = data.patient;
		patientResult = updatePatient(patientResult, "newPatient", data.patient, mpi, mylab, values);
		submit(data.record.id, patientResult);
		closeModal(true);
	};

	const radio = (name, label, fields, values) => {
		let filter = fields.filter(
			(f, index, arr) => f.customlabel && f.customlabel !== "" && arr.filter((f1, i) => f1.customlabel === f.customlabel && index >= i).length === 1
		);
		if (filter.length > 1)
			return (
				<div key={`radio-${name}-${label}`}>
					<div style={{ display: "flex", flexDirection: "column", alignItems: "baseline" }}>
						{filter.map((f, index) => (
							<span key={`filter-${name}-${f.id}`} style={{ display: "flex", justifyContent: "flex-start", alignItems: "baseline" }}>
								<Field
									style={{ margin: "5px" }}
									type="radio"
									name={name}
									value={f.id}
									checked={Object.values(values)[Object.keys(values).indexOf(name)] === f.id}
								/>
								{f.customlabel}
							</span>
						))}
					</div>{" "}
				</div>
			);
		if (filter.length === 1)
			return (
				<div key={`radio-solo-${name}-${label}`}>
					<div style={{ display: "flex", alignItems: "baseline" }}>{filter[0].customlabel}</div>
				</div>
			);
	};
	const closeModal = (onlyReinit = false) => {
		setStep(1);
		setMylab(null);
		setPage(1);
		setLarger(false);
		if (!onlyReinit) close();
	};

	const updateFieldMPI = (patient, type, field, mpi) => {
		let result = { ...patient };
		result[field] = mpi[field];
		// eval(`result.${field}= mpi[field];`);
		return result;
	};

	const updateFieldMylab = (patient, type, field, mylab) => {
		let result = { ...patient };
		if (field === "identity_number") {
			result.identification = mylab[field];
			// eval("result.identification = mylab[field];");
		} else {
			result[field] = mylab[field];
			// eval("result." + field + "= mylab[field];");
		}
		return result;
	};
	const updateFieldForm = (patient, type, field, data) => {
		let result = { ...patient };
		if (type === "mylab" && data[field] !== "" && field === "mobile_phone") {
			result.mobile_phone = data[field];
		} else if (type === "mylab" && field === "identity_number" && data[field] !== "") {
			result.identification = data[field];
		} else {
			if (data[field] !== "") result[field] = data[field];
			// eval("result." + field + "= data[field];");
		}

		return result;
	};
	const updateFieldEmpty = (patient, type, field) => {
		let result = { ...patient };
		if (type === "mylab" && field === "mobile_phone") {
			result.mobilephone = "";
		} else if (type === "mylab" && field === "identity_number") {
			result.identification = "";
		} else {
			result[field] = "";
			// eval("result." + field + "= '';");
		}
		return result;
	};

	const updatePatient = (patient, type, data, mpi, mylab, values) => {
		Object.entries(values).map((val) => {
			if (
				((type === "mylab" && val[1] !== "" && (val[0] === "email" || val[0] === "mobile_phone" || val[0] === "identity_number")) ||
					type !== "mylab") &&
				val[1] !== "" &&
				val[1] !== type
			) {
				switch (val[1]) {
					case "void":
						patient = updateFieldEmpty(patient, type, val[0]);
						break;
					case "mylab":
						patient = updateFieldMylab(patient, type, val[0], mylab);
						break;
					case "newPatient":
						patient = updateFieldForm(patient, type, val[0], data);
						break;
					case "mpi":
						patient = updateFieldMPI(patient, type, val[0], mpi);
						break;
					default:
						break;
				}
			}
			return patient;
		});
		return patient;
	};
	const getmatriculeSearchValue = (patient) => {
		if (patient)
			return patient.cns
				? patient.cns
				: patient.birth_date
				? moment(patient.birth_date, "DD/MM/YYYY").format("YYYYMMDD")
				: patient.matricule
				? patient.matricule
				: "";
		return "";
	};

	return (
		<Modal open={open} closeOnEscape closeOnDimmerClick onClose={closeModal}>
			<Formik
				enableReinitialize
				initialValues={{
					matricule: getmatriculeSearchValue(data?.patient),
					mylabGroup: "newPatient",
					birth_name: "newPatient",
					married_name: "newPatient",
					first_name: "newPatient",
					gender: "newPatient",
					birth_date: "newPatient",
					mobile_phone: "newPatient",
					email: "newPatient",
					identity_number: "newPatient",
					cns: "newPatient",
					toBeNotify: true,
					useMylab: false,
				}}
				validationSchema={schemaValidation}
			>
				{({ errors, values, setFieldValue }) => (
					<>
						<Modal.Content>
							<Form
								onKeyUp={(e) => {
									e.preventDefault();
									if (new RegExp("^\\d{8,}").test(values.matricule) || new RegExp("^[A-zÀ-ž]{3,}").test(values.matricule))
										debounce(searchByMatricule(values.matricule, 1), 700);
								}}
							>
								<Step.Group attached="top" style={{ width: "100%" }}>
									<Step disabled={step < 1} active={step === 1}>
										<Icon name="user" />
										<Step.Content>
											<Step.Title>Association ou création d'un patient</Step.Title>
											<Step.Description>Recherche d'un patient</Step.Description>
										</Step.Content>
									</Step>

									<Step disabled={step < 2} active={step === 2}>
										<Icon name="clipboard check" />
										<Step.Content>
											<Step.Title>Confirmation</Step.Title>
										</Step.Content>
									</Step>
								</Step.Group>
								{loading && (
									<Dimmer active>
										{" "}
										<Loader />{" "}
									</Dimmer>
								)}
								<Segment attached>
									{step === 1 && (
										<>
											<Segment>
												<div style={{ display: "flex", justifyContent: "center", marginBottom: "7px" }}>
													<Header as="h4" icon="list" content="Données du formulaire patient" style={{ padding: "auto" }} />
												</div>
												<div>{patientData(data.patient, "form")}</div>
											</Segment>
											<Segment>
												<div>
													Rechercher si ce patient est déjà connu du système sur base du numéro de matricule du patient ou de ses données
													personnelles.
												</div>
												<div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
													<div style={{ flex: 1, maxWidth: "150px" }}>
														<Field
															type="text"
															name="matricule"
															placeholder=""
															component={CustomInputTextComponent}
															disabled={false}
															errors={errors}
														/>
													</div>
													<Popup
														content="Cette recherche interroge MyLab"
														position="top center"
														trigger={
															<Button
																color="green"
																style={{ marginBottom: "1px", marginLeft: "5px" }}
																type="button"
																disabled={!values.matricule || values.matricule.toString().length < 3}
																onClick={(e) => {
																	if (e !== undefined) e.preventDefault();
																	setFieldValue("larger", false);
																	searchByMatricule(values.matricule, 1);
																}}
															>
																<Icon name="search" /> Rechercher
															</Button>
														}
													/>
												</div>
												{mpi && (
													<Message info>
														<Message.Header>
															<div style={{ display: "flex", justifyContent: "center", marginBottom: "7px" }}>
																<Header as="h4" icon="list" content="Données de l'annuaire national des patients" style={{ padding: "auto" }} />
															</div>
														</Message.Header>
														{patientData(mpi, "mpi")}
													</Message>
												)}
												{larger && (
													<Grid style={{ margin: 0 }}>
														<Grid.Row style={{ ...styles.roundHeaderContainer, height: 40, backgroundColor: "red" }}>
															<Grid.Column style={{ ...styles.columnFlex, justifyContent: "space-between" }} mobile={16} tablet={16} computer={16}>
																<div style={{ ...styles.columnFlex, color: "#fff", fontWeight: 600 }}>
																	<Icon name="user x" size="large" style={{ ...styles.icon, marginRight: "10px" }} />{" "}
																	<span>
																		Aucun patient trouvé dans MyLab <span style={{ textTransform: "uppercase" }}>avec ce matricule.</span>{" "}
																	</span>
																</div>
															</Grid.Column>
														</Grid.Row>
													</Grid>
												)}
											</Segment>

											{mylabList && (
												<Grid style={{ margin: 0, maxHeight: "64vh", overflow: "auto" }}>
													<Grid.Row style={{ ...styles.roundHeaderContainer, height: 40, backgroundColor: larger ? "orange" : "#43b39e" }}>
														<Grid.Column
															style={{ ...styles.columnFlex, justifyContent: "space-between", padding: "0 0 0 14px" }}
															mobile={16}
															tablet={16}
															computer={16}
														>
															<div style={{ ...styles.columnFlex, color: "#fff", fontWeight: 600, alignItems: "baseline" }}>
																<>
																	{mylabList.length > 0 && mylabPagination && Math.ceil(mylabPagination.total / 20) > 1 && (
																		<Icon name="warning sign" style={styles.icon} />
																	)}
																	{mylabList.length > 0 && mylabPagination && Math.ceil(mylabPagination.total / 20) < 2 && (
																		<Icon name="users" size="large" style={styles.icon} />
																	)}
																	{mylabList.length > 0 && mylabPagination && (
																		<>
																			<span>
																				{mylabPagination.total} patient
																				{mylabPagination.total > 1 ? "s" : ""} trouvé
																				{mylabPagination.total > 1 ? "s" : ""} dans MyLab
																				{larger ? (
																					<span style={{ textTransform: "uppercase" }}>
																						{" "}
																						avec la même date de naissance et un matricule incomplet
																					</span>
																				) : (
																					""
																				)}
																				.
																			</span>
																		</>
																	)}
																	{mylabPagination && Math.ceil(mylabPagination.total / 20) > 1 && (
																		<span style={{ paddingLeft: "5px" }}>Veuillez affiner votre recherche.</span>
																	)}
																	{mylabList.length === 0 && (
																		<>
																			<Icon name="user x" size="large" style={{ ...styles.icon, marginRight: "10px" }} />
																			<span>Aucun patient trouvé dans MyLab</span>
																		</>
																	)}
																</>
															</div>
															{mylabPagination && Math.ceil(mylabPagination.total / 20) > 1 && (
																<div
																	style={{
																		...styles.columnFlex,
																		color: "#fff",
																		fontWeight: 600,
																	}}
																>
																	<span>
																		Page {page}/{Math.ceil(mylabPagination.total / 20)}{" "}
																	</span>
																</div>
															)}
														</Grid.Column>
													</Grid.Row>
													{mylabList.map((l, index) => (
														<React.Fragment key={"fragpatientmylab" + index}>
															<Grid.Row
																key={"patientmylab" + index}
																style={mylab && mylab.id === l.id ? styles.patientListSelected : styles.patientList}
															>
																{mylab && mylab.id === l.id && (
																	<>
																		<div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "flex-start" }}>
																			<Label as="span" color="teal" ribbon>
																				Patient sélectionné
																			</Label>
																			<MainButton
																				text="Ne pas utiliser ce patient"
																				style={{ backgroundColor: "darkred" }}
																				onClick={(e) => {
																					if (e !== undefined) {
																						e.preventDefault();
																						e.stopPropagation();
																					}
																					setMylab(null);
																				}}
																			/>
																		</div>

																		<Grid.Column mobile={16} tablet={16} computer={16} style={{ textAlign: "right" }}></Grid.Column>

																		<Grid.Column mobile={16} tablet={16} computer={11}>
																			<div>
																				<span style={{ color: "black", paddingLeft: "0.5em" }}>
																					{displayGender(l, generalParameters.data)} {displayName(l)}
																				</span>
																				<span style={{ padding: "0 2em" }}>{moment().diff(l.birth_date, "years")} ans </span>
																				{l.active && (
																					<span style={{ fontSize: "0.8em" }}>
																						<Icon name="heart" /> MyLab actif
																					</span>
																				)}
																			</div>
																		</Grid.Column>
																	</>
																)}
																{(!mylab || mylab.id !== l.id) && (
																	<>
																		<Grid.Column mobile={16} tablet={5} computer={5}>
																			<div>
																				<b>
																					{displayGender(l, generalParameters.data)} {displayName(l)}
																				</b>
																				<br />
																				<span style={{ fontSize: "0.8em", fontStyle: "italic" }}>{moment().diff(l.birth_date, "years")} ans</span>
																			</div>
																		</Grid.Column>

																		<Grid.Column mobile={16} tablet={3} computer={3} style={{}}>
																			<b style={{ fontSize: "0.8em" }}>
																				{" "}
																				{l.gender === "f" ? "Née" : "Né"} le {moment(l.birth_date).format(process.env.REACT_APP_DATE_DISPLAY_FORMAT)}
																			</b>
																		</Grid.Column>

																		<Grid.Column mobile={16} tablet={3} computer={3} style={{}}>
																			<div>
																				<b style={{ fontSize: "0.8em" }}>N° {l.cns}</b>
																			</div>
																			{l.active && (
																				<span style={{ fontSize: "0.8em" }}>
																					<Icon name="heart" /> MyLab actif
																				</span>
																			)}
																		</Grid.Column>

																		<Grid.Column mobile={16} tablet={5} computer={5} style={{ textAlign: "right" }}>
																			<MainButton
																				text="Sélectionner ce patient"
																				onClick={(e) => {
																					if (e !== undefined) {
																						e.preventDefault();
																						e.stopPropagation();
																					}
																					setMylab(l);
																				}}
																			/>
																			{mylab && mylab.id === l.id && (
																				<div>
																					<span style={{ color: "darkred", textAlign: "center" }}>Patient sélectionné</span>
																					<MainButton
																						text="Ne pas utiliser ce patient"
																						style={{ backgroundColor: "darkred" }}
																						onClick={(e) => {
																							if (e !== undefined) {
																								e.preventDefault();
																								e.stopPropagation();
																							}
																							setMylab(null);
																						}}
																					/>
																				</div>
																			)}
																		</Grid.Column>
																	</>
																)}
															</Grid.Row>

															{mylab && mylab.id === l.id && (
																<Grid.Row key={"patientmylabSelected" + index} style={styles.patientListSelectedData}>
																	{patientData(mylab, "mylab")}
																</Grid.Row>
															)}
														</React.Fragment>
													))}

													{mylabPagination && Math.ceil(mylabPagination.total / 20) > 1 && (
														<Grid.Row>
															<CustomPagination
																activePage={page}
																totalPages={Math.ceil(mylabPagination.total / 20)}
																onPageChange={(e, { activePage }) => {
																	searchByMatricule(values.matricule, activePage);
																}}
															/>
														</Grid.Row>
													)}
												</Grid>
											)}
										</>
									)}

									{step === 2 && (mylab || mpi) && (
										<>
											<Segment>
												<div style={{ margin: "15px 0" }}>
													<div style={{ margin: "20px 0 40px" }}>
														{patientDataUpdated(data.patient, mylab, mpi, defineFields(data.patient, mylab, mpi), values)}
													</div>
													{!!mylab &&
														getModifiedFields(data.patient, mylab, mpi, !!mylab).filter((e) =>
															Object.entries(values).some((v) => e.field === v[0] && v[1] !== "mylab")
														).length > 0 && (
															<Message color="red" style={{ fontSize: "0.9em", textAlign: "center" }}>
																<Message.Header>! Modification de données sensibles du patient !</Message.Header>
																<div style={{ fontWeight: "bold", color: "black" }}>Vous vous apprêtez à modifier ces données :</div>
																<span style={{ fontSize: "1.4em", backgroundColor: "yellow", color: "red" }}>
																	{getModifiedFields(data.patient, mylab, mpi, !!mylab)
																		.filter((e) => Object.entries(values).some((v) => e.field === v[0] && v[1] !== "mylab"))
																		.map((f) => f.label)
																		.join(", ")}
																</span>
																<div style={{ textTransform: "uppercase", fontWeight: "bold", margin: "25px 0 0", textDecoration: "underline" }}>
																	Etes-vous sûr de vouloir modifier ce patient ?{" "}
																</div>
																<div>Cela aura un impact sur tous les dossiers de ce patient. </div>
																<div style={{ fontWeight: "bold", margin: "25px 0 0", textDecoration: "underline" }}>
																	<span style={{ textTransform: "uppercase" }}>Le patient MyLab sélectionné n’est pas le patient à prélever ?</span>
																</div>
																<div>
																	Retournez à l'étape précédente et utilisez la recherche pour trouver le bon patient ou cliquez sur 'Ne pas utiliser
																	ce patient' pour le désélectionner et en créer un nouveau.
																</div>
															</Message>
														)}
												</div>
											</Segment>
										</>
									)}
									{step === 2 && !mylab && !mpi && (
										<Message info>
											<Message.Header>Données du nouveau patient à créer</Message.Header>
											{patientData(data.patient, "form")}
										</Message>
									)}
								</Segment>
							</Form>
						</Modal.Content>

						<Modal.Actions>
							<Button
								onClick={(e) => {
									if (e !== undefined) e.preventDefault();
									closeModal();
								}}
							>
								Annuler
							</Button>

							<>
								{step > 1 && (
									<Button
										color="red"
										onClick={(e) => {
											if (e !== undefined) e.preventDefault();
											setStep(step - 1);
										}}
									>
										Précédent
									</Button>
								)}

								{mylab && step === 1 && (
									<Button
										primary
										onClick={(e) => {
											if (e !== undefined) e.preventDefault();
											values.useMylab = true;
											setStep(2);
										}}
									>
										Utiliser ce patient
									</Button>
								)}
								{!mylab && step === 1 && (
									<Button
										primary
										onClick={(e) => {
											if (e !== undefined) e.preventDefault();
											setStep(2);
										}}
									>
										Créer un nouveau patient
									</Button>
								)}
								{!mylab && step === 2 && (
									<Button
										primary
										onClick={(e) => {
											if (e !== undefined) e.preventDefault();
											handleCreatePatient(values);
										}}
									>
										Créer un nouveau patient
									</Button>
								)}
								{mylab && step === 2 && (
									<Button
										primary
										onClick={(e) => {
											if (e !== undefined) e.preventDefault();
											values.useMylab = true;
											handleUsePatient(values);
										}}
									>
										Utiliser ce patient
									</Button>
								)}
							</>
						</Modal.Actions>
					</>
				)}
			</Formik>
		</Modal>
	);
};

export default CampaignPatientMatchingModal;
